import {
    appendHighlightedText,
    createCacheKey,
    debounce,
    exerciseRequestSize,
    filterExercises,
    getJson,
    readOptionLabels,
    toggleLoading,
    track,
} from "./exercisePickerShared";
import {bindNativeModalCancel, setNativeModalOpen} from "./modalShared";

const requestSize = exerciseRequestSize;

export function findInitialExercises() {
    const prefill = window.prefill || [];
    for (let i = 0; i < prefill.length; i++) {
        if (prefill[i].request && prefill[i].request.url === "/api/exercises") {
            return prefill[i].response;
        }
    }
    return null;
}

function getItemsPerPage() {
    const innerHeight = window.innerHeight || 0;
    if (innerHeight > 900) {
        return 16;
    }
    return innerHeight > 700 ? 12 : 8;
}

export function createExerciseModal(options) {
    const root = typeof options.root === "string" ? document.querySelector(options.root) : options.root;
    if (!root) {
        return null;
    }

    const initialExercises = options.initialExercises || null;
    const itemsPerPage = getItemsPerPage();
    const searchInput = root.querySelector("[data-exercise-modal-search]");
    const searchControl = root.querySelector("[data-exercise-modal-search-control]");
    const bodypartSelect = root.querySelector("[data-exercise-modal-bodypart]");
    const categorySelect = root.querySelector("[data-exercise-modal-category]");
    const listNode = root.querySelector("[data-exercise-modal-list]");
    const listContainer = listNode ? listNode.parentNode : null;
    const emptyNode = root.querySelector("[data-exercise-modal-empty]");
    const loadingNode = root.querySelector("[data-exercise-modal-loading]");
    const moreButton = root.querySelector("[data-exercise-modal-more]");
    const moreRow = root.querySelector("[data-exercise-modal-more-row]");
    const closeNodes = root.querySelectorAll("[data-exercise-modal-close]");
    const bodypartLabels = readOptionLabels(bodypartSelect);
    const eventCategory = "calculator-exercise-modal";
    const responseCache = {};

    const state = {
        showing: false,
        loadingFirstTime: !initialExercises,
        loadingKey: null,
        searching: false,
        listMinHeight: null,
        count: initialExercises ? initialExercises.meta.count : Number.POSITIVE_INFINITY,
        bodypart: "",
        category: "",
        searchText: "",
        exercises: initialExercises ? initialExercises.data : [],
        visibleExercises: [],
        filteredCount: 0,
        hasMore: false,
        limit: itemsPerPage,
    };

    function createParams() {
        const limit = Math.ceil(state.limit / requestSize) * requestSize;
        const params = {
            limit: limit,
            "exercise.fields": "category,type,name_url,bodypart,count,aliases,icon_url",
            standard: "true",
        };

        if (state.searchText !== "") {
            params.query = state.searchText;
        }
        if (state.category !== "") {
            params.category = state.category;
        }
        if (state.bodypart !== "") {
            params.bodypart = state.bodypart;
        }
        return params;
    }

    function getFilteredExerciseList() {
        return filterExercises(state.exercises, state.category, state.bodypart, state.searchText);
    }

    function updateMinHeight() {
        if (!state.listMinHeight && listContainer && !state.loadingFirstTime) {
            state.listMinHeight = listContainer.offsetHeight;
            if (state.listMinHeight) {
                listContainer.style.minHeight = state.listMinHeight + "px";
            }
        }
    }

    function createIcon(exercise) {
        const picture = document.createElement("picture");
        picture.className = "media-left image is-32x32";

        if (exercise.icon_url) {
            const source = document.createElement("source");
            source.type = "image/webp";
            source.srcset = exercise.icon_url.replace("128.png", "64.webp") + " 2x, " + exercise.icon_url.replace("128.png", "32.webp") + " 1x";
            picture.appendChild(source);

            const image = document.createElement("img");
            image.loading = "lazy";
            image.src = exercise.icon_url.replace("128.png", "32.png");
            image.srcset = exercise.icon_url.replace("128.png", "64.png") + " 2x, " + exercise.icon_url.replace("128.png", "32.png") + " 1x";
            image.alt = exercise.name || "";
            image.width = 32;
            image.height = 32;
            picture.appendChild(image);
        }

        return picture;
    }

    function createExerciseNode(exercise) {
        const link = document.createElement("a");
        link.className = "media";
        link.href = "#";
        link.addEventListener("click", function (event) {
            event.preventDefault();
            options.onSelect(exercise);
            hideModal();
        });

        link.appendChild(createIcon(exercise));

        const content = document.createElement("span");
        content.className = "media-content";

        const name = document.createElement("span");
        name.className = "mr-1";
        appendHighlightedText(name, exercise.name, state.searchText);
        content.appendChild(name);

        const tag = document.createElement("span");
        tag.className = "tag is-small";
        tag.textContent = bodypartLabels[exercise.bodypart] || exercise.bodypart || "";
        content.appendChild(tag);

        const aliases = Array.isArray(exercise.aliases) ? exercise.aliases : [];
        if (aliases.length > 0) {
            content.appendChild(document.createElement("br"));

            const aliasText = document.createElement("span");
            aliasText.className = "has-text-black is-size-7";
            aliasText.appendChild(document.createTextNode((root.dataset.alsoKnownAs || "Also known as:") + " "));
            appendHighlightedText(aliasText, aliases.join(", "), state.searchText);
            content.appendChild(aliasText);
        }

        link.appendChild(content);
        return link;
    }

    function renderControls(exercises, hasMore) {
        exercises = exercises || state.visibleExercises;
        hasMore = typeof hasMore === "boolean" ? hasMore : state.hasMore;

        const isBusy = !!state.loadingKey || state.searching;
        toggleLoading(searchControl, isBusy);
        if (moreButton) {
            moreButton.classList.toggle("is-loading", isBusy);
            moreButton.disabled = state.loadingFirstTime || isBusy || !hasMore;
        }
        if (moreRow) {
            moreRow.classList.toggle("is-hidden", state.loadingFirstTime);
        }

        if (loadingNode) {
            loadingNode.classList.toggle("is-hidden", !state.loadingFirstTime);
        }
        if (emptyNode) {
            emptyNode.classList.toggle("is-hidden", state.loadingFirstTime || isBusy || exercises.length > 0);
        }
    }

    function renderList(exercises, hasMore) {
        if (!listNode) {
            renderControls(exercises, hasMore);
            return;
        }

        listNode.innerHTML = "";
        const fragment = document.createDocumentFragment();

        for (let i = 0; i < exercises.length; i++) {
            fragment.appendChild(createExerciseNode(exercises[i]));
        }

        listNode.appendChild(fragment);
        if (emptyNode) {
            listNode.appendChild(emptyNode);
        }
        if (moreRow) {
            listNode.appendChild(moreRow);
        }
        renderControls(exercises, hasMore);
    }

    function updateVisibleExercises(filteredExercises) {
        const exercises = filteredExercises.slice(0, state.limit);
        const hasMore = state.count > state.limit || filteredExercises.length > state.limit;

        state.visibleExercises = exercises;
        state.filteredCount = filteredExercises.length;
        state.hasMore = hasMore;

        return {
            exercises: exercises,
            hasMore: hasMore,
        };
    }

    function renderFiltered(filteredExercises) {
        const visible = updateVisibleExercises(filteredExercises);
        renderList(visible.exercises, visible.hasMore);
    }

    function render() {
        renderFiltered(getFilteredExerciseList());
    }

    function loadExercises() {
        const params = createParams();
        const loadingKey = createCacheKey("exercises", params);

        if (responseCache[loadingKey]) {
            state.loadingKey = null;
            state.exercises = responseCache[loadingKey].data || [];
            state.count = responseCache[loadingKey].meta ? responseCache[loadingKey].meta.count : 0;
            state.loadingFirstTime = false;
            state.searching = false;
            render();
            return Promise.resolve();
        }

        state.loadingKey = loadingKey;
        renderControls();

        return getJson("exercises", params).then(function (response) {
            responseCache[loadingKey] = response;
            if (state.loadingKey === loadingKey) {
                state.exercises = response.data || [];
                state.count = response.meta ? response.meta.count : 0;
            }
        }).catch(function () {
            if (state.loadingKey === loadingKey) {
                state.count = 0;
            }
        }).then(function () {
            if (state.loadingKey === loadingKey) {
                state.loadingKey = null;
                state.searching = false;
                state.loadingFirstTime = false;
                render();
            } else {
                renderControls();
            }
        });
    }

    function loadExercisesIfRequired(isSubset) {
        const filteredExercises = getFilteredExerciseList();
        if (state.showing && (!isSubset || state.limit > filteredExercises.length)) {
            return loadExercises();
        }
        renderFiltered(filteredExercises);
        return Promise.resolve();
    }

    function resetLimit(isSubset) {
        updateMinHeight();
        state.limit = itemsPerPage;
        return loadExercisesIfRequired(isSubset);
    }

    const loadSearchResults = debounce(function () {
        loadExercises();
    }, 200);

    function updateSearch(value) {
        const oldValue = state.searchText;
        const isSubset = oldValue === "" || value.indexOf(oldValue) === 0;
        state.searchText = value;
        state.limit = itemsPerPage;
        updateMinHeight();
        const filteredExercises = getFilteredExerciseList();
        renderFiltered(filteredExercises);

        if (state.showing && (!isSubset || state.limit > filteredExercises.length)) {
            state.searching = true;
            renderControls();
            loadSearchResults();
        } else {
            state.searching = false;
            loadSearchResults.cancel();
            renderControls();
        }
    }

    function onKeyDown(event) {
        if (event.key === "Escape" || event.keyCode === 27) {
            hideModal();
        }
    }

    function resetModal() {
        loadSearchResults.cancel();
        state.limit = itemsPerPage;
        state.searchText = "";
        state.category = "";
        state.bodypart = "";
        state.searching = false;
        state.loadingKey = null;
        state.listMinHeight = null;
        state.loadingFirstTime = !initialExercises;
        state.exercises = initialExercises ? initialExercises.data : [];
        state.visibleExercises = [];
        state.filteredCount = 0;
        state.hasMore = false;
        state.count = initialExercises ? initialExercises.meta.count : Number.POSITIVE_INFINITY;
        if (listContainer) {
            listContainer.style.minHeight = "";
        }
        if (searchInput) {
            searchInput.value = "";
        }
        if (categorySelect) {
            categorySelect.value = "";
        }
        if (bodypartSelect) {
            bodypartSelect.value = "";
        }
        if (state.showing) {
            render();
        } else if (listNode) {
            listNode.innerHTML = "";
            renderControls();
        }
    }

    function showModal() {
        state.showing = true;
        setNativeModalOpen(root, true);
        document.addEventListener("keydown", onKeyDown);
        render();

        if (!initialExercises && state.loadingFirstTime) {
            loadExercises();
        }

        if (searchInput && window.innerWidth > 768) {
            setTimeout(function () {
                searchInput.focus();
            }, 0);
        }
    }

    function hideModal() {
        document.removeEventListener("keydown", onKeyDown);
        state.showing = false;
        setNativeModalOpen(root, false);
        resetModal();
    }

    if (searchInput) {
        searchInput.addEventListener("input", function (event) {
            updateSearch(event.target.value);
        });
        searchInput.addEventListener("keydown", function (event) {
            if (event.key === "Enter") {
                event.preventDefault();
                const firstExercise = state.visibleExercises[0];
                if (firstExercise) {
                    options.onSelect(firstExercise);
                    hideModal();
                }
            }
        });
    }

    if (categorySelect) {
        categorySelect.addEventListener("change", function (event) {
            const oldValue = state.category;
            state.category = event.target.value;
            resetLimit(oldValue === "");
            if (state.category) {
                track("filter-group", eventCategory, state.category);
            }
        });
    }

    if (bodypartSelect) {
        bodypartSelect.addEventListener("change", function (event) {
            const oldValue = state.bodypart;
            state.bodypart = event.target.value;
            resetLimit(oldValue === "");
            if (state.bodypart) {
                track("filter-body-part", eventCategory, state.bodypart);
            }
        });
    }

    if (moreButton) {
        moreButton.addEventListener("click", function (event) {
            if (state.loadingFirstTime || state.loadingKey || state.searching || !state.hasMore) {
                event.preventDefault();
                return;
            }
            state.limit += itemsPerPage;
            loadExercisesIfRequired(true);
        });
    }

    for (let i = 0; i < closeNodes.length; i++) {
        closeNodes[i].addEventListener("click", hideModal);
    }

    bindNativeModalCancel(root, hideModal, function () {
        return state.showing;
    });

    resetModal();

    return {
        showModal: showModal,
        hideModal: hideModal,
    };
}
